import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Loader2, LogIn, Zap, MessageSquare, Calendar,
  ChevronLeft, ChevronRight, BarChart2, Clock, RefreshCw,
  AlertCircle, Filter, TrendingUp, User, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ── Helpers ───────────────────────────────────────────────────────────────────

const ACTION_META = {
  login:        { label: 'Login',        icon: LogIn,         color: 'text-green-400',  bg: 'bg-green-500/10',  border: 'border-green-500/20'  },
  quick_action: { label: 'Quick Action', icon: Zap,           color: 'text-[#00a1df]', bg: 'bg-[#00a1df]/10', border: 'border-[#00a1df]/20' },
  chat:         { label: 'Chat libre',   icon: MessageSquare, color: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/20'   },
  eliminacion:  { label: 'Eliminación',  icon: Trash2,        color: 'text-red-400',    bg: 'bg-red-500/10',    border: 'border-red-500/20'    },
};

const fmt = (isoStr) => {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr);
    return d.toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    });
  } catch { return isoStr; }
};

const fmtDate = (isoStr) => {
  if (!isoStr) return '—';
  try {
    return new Date(isoStr).toLocaleDateString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  } catch { return isoStr; }
};

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

// ── Sub-components ────────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, color = 'text-gray-400' }) => (
  <div className="bg-[#0F1117] border border-gray-800 rounded-xl p-4 flex items-center gap-3">
    <Icon className={`w-6 h-6 ${color} opacity-70 shrink-0`} />
    <div>
      <p className={`text-xl font-black ${color}`}>{value ?? '—'}</p>
      <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">{label}</p>
    </div>
  </div>
);

const ActionBadge = ({ type }) => {
  const meta = ACTION_META[type] || ACTION_META.chat;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${meta.bg} ${meta.color} ${meta.border}`}>
      <Icon className="w-2.5 h-2.5" />
      {meta.label}
    </span>
  );
};

const QuickActionBar = ({ title, count, max }) => {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center text-xs">
        <span className="text-gray-300 truncate pr-2 font-medium">{title || 'Sin título'}</span>
        <span className="text-[#00a1df] font-black shrink-0">{count}x</span>
      </div>
      <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#00a1df] to-[#0091ca] rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

// ── Modal principal ───────────────────────────────────────────────────────────

const UserHistoryModal = ({ user, token, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('logs');
  // Default vacío = sin filtro de fecha → muestra TODO el historial
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const fetchHistory = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: PAGE_SIZE,
        skip: page * PAGE_SIZE,
      });
      // Solo enviar filtros si están seteados
      if (dateFrom)     params.set('date_from', dateFrom);
      if (dateTo)       params.set('date_to', dateTo);
      if (actionFilter) params.set('action_type', actionFilter);

      const res = await fetch(
        `${API_URL}/api/admin/users/${user.id}/history?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Error ${res.status} al cargar historial`);
      }
      const json = await res.json();
      // Debug: loguear en consola para facilitar diagnóstico
      console.log('[UserHistory] response debug:', json.debug);
      setData(json);
    } catch (err) {
      console.error('[UserHistory] fetch error:', err);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id, token, dateFrom, dateTo, actionFilter, page]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Atajos de fecha
  const applyRange = (from, to) => {
    setDateFrom(from);
    setDateTo(to);
    setPage(0);
  };

  const stats = data?.stats || {};
  const logs  = data?.logs  || [];
  const pagination = data?.pagination || {};
  const topQA = data?.top_quick_actions || [];
  const debugInfo = data?.debug || null;
  // Mostrar aviso si el filtro de fecha está cortando datos
  const filtroEstaRecortando = debugInfo &&
    debugInfo.total_sin_filtros > 0 &&
    debugInfo.total_con_filtros === 0 &&
    (dateFrom || dateTo);

  const totalLogins  = stats.login?.count        ?? 0;
  const totalQA      = stats.quick_action?.count ?? 0;
  const totalChats   = stats.chat?.count         ?? 0;
  const maxQA        = topQA[0]?.count || 1;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-[#1A1D27] border border-gray-700 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">

        {/* ── Header ── */}
        <div className="px-6 py-4 border-b border-gray-800 bg-[#232733] flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#00a1df] to-[#006fa0] flex items-center justify-center text-white font-black text-sm shadow-lg border border-[#00a1df]/20 shrink-0">
              {user.name?.[0]?.toUpperCase() || '?'}
            </div>
            <div>
              <h3 className="text-base font-bold text-white leading-none">
                Historial de actividad — {user.name}
              </h3>
              <p className="text-[11px] text-gray-500 mt-0.5">{user.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-1.5 hover:bg-gray-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Filtros ── */}
        <div className="px-6 py-3 border-b border-gray-800 bg-[#1E2130] shrink-0">
          <div className="flex flex-wrap items-center gap-3">
            {/* Atajos rápidos */}
            <div className="flex items-center gap-1.5">
              {[
                { label: 'Todo',    from: '',            to: ''        },
                { label: 'Hoy',     from: today(),       to: today()   },
                { label: '7 días',  from: daysAgo(7),    to: today()   },
                { label: '30 días', from: daysAgo(30),   to: today()   },
                { label: '3 meses', from: daysAgo(90),   to: today()   },
              ].map(r => (
                <button
                  key={r.label}
                  onClick={() => applyRange(r.from, r.to)}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all ${
                    dateFrom === r.from && dateTo === r.to
                      ? 'bg-[#00a1df]/20 text-[#00a1df] border-[#00a1df]/40'
                      : 'bg-gray-800 text-gray-500 border-gray-700 hover:text-gray-300 hover:border-gray-500'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1.5 ml-auto">
              <Calendar className="w-3.5 h-3.5 text-gray-500 shrink-0" />
              <input
                type="date" value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); setPage(0); }}
                className="bg-[#0F1117] border border-gray-700 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-[#00a1df] transition-colors"
              />
              <span className="text-gray-600 text-xs">→</span>
              <input
                type="date" value={dateTo}
                onChange={e => { setDateTo(e.target.value); setPage(0); }}
                className="bg-[#0F1117] border border-gray-700 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-[#00a1df] transition-colors"
              />

              <Filter className="w-3.5 h-3.5 text-gray-500 ml-2 shrink-0" />
              <select
                value={actionFilter}
                onChange={e => { setActionFilter(e.target.value); setPage(0); }}
                className="bg-[#0F1117] border border-gray-700 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-[#00a1df] transition-colors"
              >
                <option value="">Todas las acciones</option>
                <option value="login">Login</option>
                <option value="quick_action">Quick Action</option>
                <option value="chat">Chat libre</option>
                <option value="eliminacion">Eliminaciones</option>
              </select>

              <button
                onClick={() => { setPage(0); fetchHistory(); }}
                disabled={loading}
                className="p-1.5 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700 transition-all"
                title="Recargar"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="px-6 pt-3 pb-0 border-b border-gray-800 shrink-0 flex gap-4">
          {[
            { id: 'logs',  label: 'Registros',    icon: Clock },
            { id: 'stats', label: 'Estadísticas', icon: BarChart2 },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 pb-2.5 text-xs font-bold border-b-2 transition-all ${
                tab === t.id
                  ? 'text-[#00a1df] border-[#00a1df]'
                  : 'text-gray-500 border-transparent hover:text-gray-300'
              }`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
              {t.id === 'logs' && pagination.total != null && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-gray-700 text-gray-400 text-[9px] font-black">
                  {pagination.total}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Banner: filtro recortando datos ── */}
        {filtroEstaRecortando && (
          <div className="px-5 py-2 bg-yellow-500/10 border-b border-yellow-500/20 flex items-center gap-2 text-xs text-yellow-400 shrink-0">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            El filtro de fecha no incluye los {debugInfo.total_sin_filtros} registros existentes.
            <button onClick={() => applyRange('', '')} className="ml-auto font-bold underline hover:text-yellow-300">
              Ver todo el historial
            </button>
          </div>
        )}

        {/* ── Contenido scrolleable ── */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {loading && !data ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-600">
              <Loader2 className="w-8 h-8 animate-spin" />
              <p className="text-sm">Cargando historial...</p>
            </div>
          ) : !data ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-600">
              <AlertCircle className="w-8 h-8" />
              <p className="text-sm">No se pudo cargar el historial</p>
            </div>
          ) : (
            <>
              {/* ══ TAB: LOGS ══ */}
              {tab === 'logs' && (
                <div>
                  {logs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-600">
                      <Clock className="w-8 h-8 opacity-30" />
                      <p className="text-sm">Sin actividad en este período</p>
                    </div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 z-10">
                        <tr className="bg-[#0F1117] border-b border-gray-800 text-gray-500 uppercase text-[9px] font-black tracking-widest">
                          <th className="px-5 py-2.5 text-left">Fecha y hora</th>
                          <th className="px-4 py-2.5 text-left">Tipo</th>
                          <th className="px-4 py-2.5 text-left">Detalle</th>
                          <th className="px-4 py-2.5 text-left">Área</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((log, i) => (
                          <tr
                            key={log.id || i}
                            className="border-b border-gray-800/50 hover:bg-gray-800/20 transition-colors"
                          >
                            <td className="px-5 py-2.5 font-mono text-gray-400 whitespace-nowrap">
                              {fmt(log.timestamp)}
                            </td>
                            <td className="px-4 py-2.5">
                              <ActionBadge type={log.action_type} />
                            </td>
                            <td className="px-4 py-2.5 text-gray-300 max-w-[280px]">
                              {log.action_type === 'quick_action' ? (
                                <span className="font-medium">{log.quick_action_title || log.quick_action_id || '—'}</span>
                              ) : log.action_type === 'login' ? (
                                <span className="text-green-400/70 italic">Ingresó al sistema</span>
                              ) : log.action_type === 'eliminacion' ? (
                                <span
                                  className="text-red-400/80 block truncate"
                                  title={(log.target_ids || []).length ? `IDs: ${(log.target_ids || []).join(', ')}` : undefined}
                                >
                                  {log.detalle || 'Eliminó un registro'}
                                </span>
                              ) : (
                                <span className="truncate block text-gray-400">{log.message_preview || '—'}</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5">
                              {log.area || log.tool_key ? (
                                <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-gray-800 text-gray-400 border border-gray-700 capitalize">
                                  {log.area || log.tool_key}
                                </span>
                              ) : (
                                <span className="text-gray-700">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* ══ TAB: STATS ══ */}
              {tab === 'stats' && (
                <div className="p-6 space-y-6">
                  {/* Totales */}
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-3.5 h-3.5 text-[#00a1df]" />
                      Resumen del período
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <StatCard icon={User}          label="Total eventos"  value={pagination.total}  color="text-gray-300" />
                      <StatCard icon={LogIn}         label="Logins"         value={totalLogins}       color="text-green-400" />
                      <StatCard icon={Zap}           label="Quick Actions"  value={totalQA}           color="text-[#00a1df]" />
                      <StatCard icon={MessageSquare} label="Chats libres"   value={totalChats}        color="text-blue-400" />
                    </div>
                  </div>

                  {/* Última actividad por tipo */}
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-3 flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-[#00a1df]" />
                      Última actividad por tipo
                    </h4>
                    <div className="space-y-2">
                      {Object.entries(ACTION_META).map(([type, meta]) => {
                        const Icon = meta.icon;
                        const s = stats[type];
                        return (
                          <div key={type} className={`flex items-center gap-3 p-3 rounded-xl border ${meta.bg} ${meta.border}`}>
                            <Icon className={`w-4 h-4 ${meta.color} shrink-0`} />
                            <div className="flex-1 min-w-0">
                              <span className={`text-xs font-bold ${meta.color}`}>{meta.label}</span>
                              {s ? (
                                <p className="text-[11px] text-gray-500 mt-0.5">
                                  {s.count} {s.count === 1 ? 'vez' : 'veces'} · última: {fmtDate(s.last_at)}
                                </p>
                              ) : (
                                <p className="text-[11px] text-gray-600 mt-0.5">Sin actividad en el período</p>
                              )}
                            </div>
                            <span className={`text-lg font-black ${meta.color} opacity-70`}>{s?.count ?? 0}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Top Quick Actions */}
                  {topQA.length > 0 && (
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-3 flex items-center gap-2">
                        <Zap className="w-3.5 h-3.5 text-[#00a1df]" />
                        Quick Actions más usadas
                      </h4>
                      <div className="bg-[#0F1117] border border-gray-800 rounded-xl p-4 space-y-3">
                        {topQA.map((qa, i) => (
                          <QuickActionBar
                            key={qa._id || i}
                            title={qa.title || qa._id}
                            count={qa.count}
                            max={maxQA}
                          />
                        ))}
                      </div>
                      <p className="text-[10px] text-gray-600 mt-2 text-center">
                        Última usada: {fmtDate(topQA[0]?.last_used)}
                      </p>
                    </div>
                  )}

                  {topQA.length === 0 && totalQA === 0 && (
                    <div className="flex flex-col items-center gap-3 py-8 text-gray-600">
                      <Zap className="w-8 h-8 opacity-20" />
                      <p className="text-sm">No se ejecutaron quick actions en este período</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer / Paginación ── */}
        {tab === 'logs' && data && pagination.total > 0 && (
          <div className="px-6 py-3 border-t border-gray-800 bg-[#232733] shrink-0 flex items-center justify-between">
            <p className="text-[10px] text-gray-500">
              Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, pagination.total)} de{' '}
              <span className="text-[#00a1df] font-bold">{pagination.total}</span> registros
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                className="p-1.5 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-gray-400 font-bold px-2">
                Pág. {page + 1} / {Math.max(1, Math.ceil(pagination.total / PAGE_SIZE))}
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={!pagination.has_more || loading}
                className="p-1.5 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserHistoryModal;
